import html2canvas from 'html2canvas';
import { IMAGE_CONSTANTS, TIME_CONSTANTS } from './constants';
import { formatDateToString } from './dateUtils';

/**
 * Captures an HTML element and downloads it as an A4-sized image (landscape orientation)
 * @param element - The HTML element to capture
 * @param filename - The name for the downloaded file
 */
export const captureAndDownloadAsA4Image = async (element: HTMLElement, filename: string, signal?: AbortSignal): Promise<void> => {
  // Store original scroll positions and styles for cleanup
  const scrollableElements: { element: Element; scrollTop: number; scrollLeft: number }[] = [];
  const originalStyles: { element: HTMLElement; overflow: string }[] = [];
  
  try {
    // Find the actual table element to get true dimensions
    const table = element.querySelector('table') || element.querySelector('[role="table"]') || element;
    
    // Store original scroll positions for all scrollable ancestors
    let currentElement: Element | null = element;
    
    while (currentElement) {
      if (currentElement.scrollWidth > currentElement.clientWidth || 
          currentElement.scrollHeight > currentElement.clientHeight) {
        scrollableElements.push({
          element: currentElement,
          scrollTop: currentElement.scrollTop,
          scrollLeft: currentElement.scrollLeft
        });
        currentElement.scrollTop = 0;
        currentElement.scrollLeft = 0;
      }
      currentElement = currentElement.parentElement;
    }

    // Also reset any overflow hidden that might clip content
    let parent = element.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      if (style.overflow !== 'visible') {
        originalStyles.push({
          element: parent,
          overflow: parent.style.overflow
        });
        parent.style.overflow = 'visible';
      }
      parent = parent.parentElement;
    }

    // Wait for layout to stabilize with proper cleanup
    await new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new Error('Operation aborted'));
        return;
      }
      
      const timeoutId = setTimeout(resolve, TIME_CONSTANTS.LAYOUT_STABILIZATION_DELAY);
      
      if (signal) {
        signal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(new Error('Operation aborted'));
        });
      }
    });
    
    // Get the full dimensions of the content
    const fullWidth = Math.max(element.scrollWidth, element.offsetWidth, table.scrollWidth || 0);
    const fullHeight = Math.max(element.scrollHeight, element.offsetHeight, table.scrollHeight || 0);
    
    console.log('Capturing dimensions:', { fullWidth, fullHeight });
    
    // Capture the element with full dimensions
    const canvas = await html2canvas(element, {
      useCORS: true,
      allowTaint: true,
      background: '#ffffff',
      width: fullWidth,
      height: fullHeight,
      logging: false,
    });

    // Restore original scroll positions and styles in finally block to ensure cleanup
    // This is now handled in the finally block below

    // A4 landscape dimensions in pixels at 150 DPI (optimized for wide tables)
    const A4_WIDTH = IMAGE_CONSTANTS.A4_WIDTH;
    const A4_HEIGHT = IMAGE_CONSTANTS.A4_HEIGHT;

    // Create a new canvas with A4 landscape dimensions
    const a4Canvas = document.createElement('canvas');
    a4Canvas.width = A4_WIDTH;
    a4Canvas.height = A4_HEIGHT;
    const ctx = a4Canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }

    // Fill with white background
    ctx.fillStyle = IMAGE_CONSTANTS.DEFAULT_BACKGROUND;
    ctx.fillRect(0, 0, A4_WIDTH, A4_HEIGHT);

    // Calculate scaling to fit content in A4 landscape with padding
    const padding = IMAGE_CONSTANTS.CANVAS_PADDING;
    const availableWidth = A4_WIDTH - (padding * 2);
    const availableHeight = A4_HEIGHT - (padding * 2);
    
    const scaleX = availableWidth / canvas.width;
    const scaleY = availableHeight / canvas.height;
    const scale = Math.min(scaleX, scaleY); // Use the smaller scale to ensure it fits

    // Calculate centered position
    const scaledWidth = canvas.width * scale;
    const scaledHeight = canvas.height * scale;
    const x = (A4_WIDTH - scaledWidth) / 2;
    const y = (A4_HEIGHT - scaledHeight) / 2;

    // Draw the captured content onto the A4 canvas
    ctx.drawImage(canvas, x, y, scaledWidth, scaledHeight);

    // Convert to blob and download
    a4Canvas.toBlob((blob) => {
      if (!blob) {
        throw new Error('Failed to create image blob');
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png', IMAGE_CONSTANTS.IMAGE_QUALITY);

  } catch (error) {
    console.error('Error capturing image:', error);
    throw new Error('Failed to capture and download image');
  } finally {
    // Always restore original scroll positions and styles, even if an error occurs
    try {
      scrollableElements.forEach(({ element, scrollTop, scrollLeft }) => {
        element.scrollTop = scrollTop;
        element.scrollLeft = scrollLeft;
      });

      originalStyles.forEach(({ element, overflow }) => {
        element.style.overflow = overflow;
      });
    } catch (restoreError) {
      console.warn('Failed to restore some element styles:', restoreError);
    }
  }
};

/**
 * Formats a date for use in filenames
 * @param date - The date to format
 * @returns A filename-safe date string
 */
export const formatDateForFilename = (date: Date): string => {
  return formatDateToString(date); // Returns YYYY-MM-DD format (SG-local, avoids UTC day shift)
}; 